// src/protocol.ts

export interface TrailPoint {
  lat: number;
  lng: number;
  timestamp: number;
}

export interface LocationMessage {
  type: 'location';
  lat: number;
  lng: number;
  heading: number | null;
  speed: number | null;
  timestamp: number;
  trail: TrailPoint[];
  peerId: string;
  name?: string;
  /** Sender's chosen car color. Receivers must validate against CAR_COLORS
      (see car-colors.ts) before rendering — never trust it raw. */
  color?: string;
  /** Body silhouette. Receivers ignore anything outside CAR_SHAPES. */
  shape?: string;
}

export interface ChatMessage {
  type: 'chat';
  text: string;
  timestamp: number;
  peerId: string;
  name?: string;
  /** Present on new clients. Receivers ack this id. Old clients omit it. */
  id?: string;
}

/** The other app accepted the chat message. Not a read receipt. */
export interface ChatAckMessage {
  type: 'chat-ack';
  messageId: string;
  peerId: string;
}

export interface PauseMessage {
  type: 'pause';
  paused: boolean;
  peerId: string;
}

export interface HostInfoMessage {
  type: 'host-info';
  hostPeerId: string;
  checkWord: string | null;
}

export interface CheckWordMessage {
  type: 'check-word';
  peerId: string;
  checkWord: string | null;
}

export interface KickMessage {
  type: 'kick';
  peerId: string;
  reason: string;
}

export interface DestinationMessage {
  type: 'destination';
  peerId: string;
  lat: number | null;  // null = cleared
  lng: number | null;
  label: string;       // display name; empty string when cleared
}

export interface RoomHistoryMessage {
  type: 'room-history';
  messages: ChatMessage[];
}

export type P2PMessage =
  | LocationMessage
  | ChatMessage
  | ChatAckMessage
  | PauseMessage
  | HostInfoMessage
  | CheckWordMessage
  | KickMessage
  | DestinationMessage
  | RoomHistoryMessage;

export function isLocationMessage(msg: unknown): msg is LocationMessage {
  return (
    typeof msg === 'object' && msg !== null &&
    (msg as Record<string, unknown>).type === 'location' &&
    typeof (msg as Record<string, unknown>).lat === 'number' &&
    typeof (msg as Record<string, unknown>).lng === 'number' &&
    typeof (msg as Record<string, unknown>).peerId === 'string'
  );
}

export function isChatMessage(msg: unknown): msg is ChatMessage {
  if (
    typeof msg !== 'object' || msg === null ||
    (msg as Record<string, unknown>).type !== 'chat' ||
    typeof (msg as Record<string, unknown>).text !== 'string' ||
    typeof (msg as Record<string, unknown>).peerId !== 'string'
  ) {
    return false;
  }
  const id = (msg as Record<string, unknown>).id;
  return id === undefined || (typeof id === 'string' && id.length > 0 && id.length <= 80);
}

export function isChatAckMessage(msg: unknown): msg is ChatAckMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as Record<string, unknown>;
  return (
    m.type === 'chat-ack' &&
    typeof m.messageId === 'string' && m.messageId.length > 0 && m.messageId.length <= 80 &&
    typeof m.peerId === 'string' && m.peerId.length > 0
  );
}

export function isPauseMessage(msg: unknown): msg is PauseMessage {
  return (
    typeof msg === 'object' && msg !== null &&
    (msg as Record<string, unknown>).type === 'pause' &&
    typeof (msg as Record<string, unknown>).paused === 'boolean' &&
    typeof (msg as Record<string, unknown>).peerId === 'string'
  );
}

export function isHostInfoMessage(msg: unknown): msg is HostInfoMessage {
  return (
    typeof msg === 'object' && msg !== null &&
    (msg as Record<string, unknown>).type === 'host-info' &&
    typeof (msg as Record<string, unknown>).hostPeerId === 'string' &&
    (((msg as Record<string, unknown>).checkWord === null) ||
      typeof (msg as Record<string, unknown>).checkWord === 'string')
  );
}

export function isCheckWordMessage(msg: unknown): msg is CheckWordMessage {
  return (
    typeof msg === 'object' && msg !== null &&
    (msg as Record<string, unknown>).type === 'check-word' &&
    typeof (msg as Record<string, unknown>).peerId === 'string' &&
    (((msg as Record<string, unknown>).checkWord === null) ||
      typeof (msg as Record<string, unknown>).checkWord === 'string')
  );
}

export function isKickMessage(msg: unknown): msg is KickMessage {
  return (
    typeof msg === 'object' && msg !== null &&
    (msg as Record<string, unknown>).type === 'kick' &&
    typeof (msg as Record<string, unknown>).peerId === 'string' &&
    typeof (msg as Record<string, unknown>).reason === 'string'
  );
}

export function isDestinationMessage(msg: unknown): msg is DestinationMessage {
  const m = msg as Record<string, unknown>;
  return (
    typeof msg === 'object' && msg !== null &&
    m.type === 'destination' &&
    typeof m.peerId === 'string' &&
    (m.lat === null || typeof m.lat === 'number') &&
    (m.lng === null || typeof m.lng === 'number') &&
    typeof m.label === 'string'
  );
}

export function isRoomHistoryMessage(msg: unknown): msg is RoomHistoryMessage {
  const m = msg as Record<string, unknown>;
  return (
    typeof msg === 'object' && msg !== null &&
    m.type === 'room-history' &&
    Array.isArray(m.messages)
  );
}

/**
 * Safely parse a JSON string into a P2PMessage, or return null on failure.
 */
export function parseMessage(raw: string): P2PMessage | null {
  try {
    const obj = JSON.parse(raw) as unknown;
    if (
      isLocationMessage(obj) ||
      isChatMessage(obj) ||
      isChatAckMessage(obj) ||
      isPauseMessage(obj) ||
      isHostInfoMessage(obj) ||
      isCheckWordMessage(obj) ||
      isKickMessage(obj) ||
      isDestinationMessage(obj) ||
      isRoomHistoryMessage(obj)
    ) {
      return obj;
    }
    return null;
  } catch {
    return null;
  }
}

/** Serialize a P2PMessage to JSON string for transmission */
export function serializeMessage(msg: P2PMessage): string {
  return JSON.stringify(msg);
}
